/**
 * TraceEvent[] → JourneyStep[] — the customer-facing REDUCED history of a run.
 *
 * The portal deliberately does not show the engineering trace (tool ids,
 * token counts, raw payloads — that's the admin portal's job). Instead this
 * module folds the same adapted event stream the Run Theater consumes into a
 * short list of business-readable milestones: "Run started", "Reviewed your
 * request", "Working — analysis · 5 hands-on tasks", "Asked you a question",
 * "Packaged the deliverable", "Delivered".
 *
 * Built on the theater's Beat layer (`eventsToBeats`) so stage language and
 * history language stay in sync, and pure for the same reason beats.ts is:
 * same events in, same steps out — trivially unit-testable, recomputed per
 * render, no clocks and no state.
 */

import type { IconName, TraceEvent } from '@evt/flow-stream-ui'
import { journey as enJourney } from '../i18n/messages/en/journey'
import { agentsInOrder, type BeatStrings, eventsToBeats, MAIN_LANE, shortAgent } from '../theater/beats'
import type { Beat } from '../theater/types'

/**
 * Every human-visible string the journey can produce. The English pack lives
 * in the i18n catalog (`messages/en/journey.ts`) and doubles as the default,
 * so tests and legacy call sites keep working untouched.
 */
export interface JourneyStrings {
  runStarted: string
  reviewedRequest: string
  gatheredAdditionalMaterials: string
  attachmentsChip: (n: number) => string
  referenceDocumentsChip: (n: number) => string
  working: string
  workAnalysis: string
  workDrafting: string
  workHandsOnTasks: (n: number) => string
  workOutsideLookups: (n: number) => string
  workNotesUpdated: string
  specialistTeamAtWork: string
  specialistTeamWorking: (n: number) => string
  specialistTeamDone: (n: number) => string
  specialistProgress: (done: number, total: number, failed: number) => string
  moreChip: (n: number) => string
  askedYouAQuestion: string
  askedYouAQuestionRound: (round: number) => string
  noReplyInTime: string
  cancelledByYou: string
  youReplied: string
  hitASnag: string
  hitASnagTimes: (n: number) => string
  packagedDeliverable: string
  filesChip: (n: number) => string
  delivered: string
  deliveredWithNotes: string
  runFailedTitle: string
  runFailedDetail: string
}

export type JourneyStepStatus = 'done' | 'active' | 'waiting' | 'success' | 'error'

export interface JourneyStep {
  /** Stable within one events array — used as the React key. */
  id: string
  icon: IconName
  title: string
  detail?: string
  chips?: string[]
  atMs: number
  status: JourneyStepStatus
}

/** Aggregate counts for the terminal summary strip. */
export interface RunStats {
  /** First-beat → last-beat span; null when the run has no dated activity yet. */
  durationMs: number | null
  /** Questions the agent asked (HITL rounds). */
  questions: number
  /** Distinct specialists that worked on the run. */
  specialists: number
  /** Knowledge volumes consulted. */
  sources: number
  /** Attachments the requester supplied (launch + replies). */
  attachmentsIn: number
  /** Hands-on tasks + outside lookups across all lanes. */
  workItems: number
  retries: number
}

/** Work-segment accumulator between two milestones (main lane only). */
interface WorkAcc {
  atMs: number
  seq: number
  thoughts: number
  drafts: number
  bench: number
  lookups: number
  memory: number
}

interface SpecialistsAcc {
  step: JourneyStep
  /** lane → finished? */
  lanes: Map<string, 'running' | 'success' | 'failure'>
  briefs: number
}

export function eventsToJourney(
  events: TraceEvent[],
  opts: { live: boolean; strings?: JourneyStrings }
): JourneyStep[] {
  const m = opts.strings ?? enJourney
  const beats = eventsToBeats(events)
  const prompts = questionPromptsBySeq(events)
  const replies = replyMetaBySeq(events)

  const steps: JourneyStep[] = []
  const retryCounts = new Map<string, number>()
  let work: WorkAcc | null = null
  let specialists: SpecialistsAcc | null = null
  let prep: JourneyStep | null = null
  let started = false
  let prepEverSeen = false
  let questions = 0

  const flushWork = (): void => {
    if (!work) return
    const step = workStep(work, m)
    if (step) steps.push(step)
    work = null
  }

  const openWork = (b: Beat): WorkAcc => {
    if (!work) work = { atMs: b.atMs, seq: b.seq, thoughts: 0, drafts: 0, bench: 0, lookups: 0, memory: 0 }
    return work
  }

  for (const b of beats) {
    switch (b.kind) {
      case 'enter':
        // Resume legs re-emit a request_received — the run only starts once.
        if (started) break
        started = true
        steps.push({ id: `start:${b.seq}`, icon: 'play', title: m.runStarted, atMs: b.atMs, status: 'done' })
        break

      case 'orient':
      case 'collect_mail':
      case 'fetch_books': {
        // Merge the pre-flight trio into one "reviewed your request" step. A
        // resume leg re-resolves config (`orient`) — engine plumbing, skip it;
        // but reply attachments / re-fetched knowledge after the first prep
        // get their own "gathered additional materials" step.
        if (prep && steps[steps.length - 1] !== prep) prep = null
        if (b.kind === 'orient' && prepEverSeen && !prep) break
        if (!prep) {
          prep = {
            id: `prep:${b.seq}`,
            icon: 'knowledge',
            title: prepEverSeen ? m.gatheredAdditionalMaterials : m.reviewedRequest,
            atMs: b.atMs,
            status: 'done',
            chips: []
          }
          prepEverSeen = true
          flushWork()
          steps.push(prep)
        }
        if (b.kind === 'collect_mail' && b.count) prep.chips?.push(m.attachmentsChip(b.count))
        if (b.kind === 'fetch_books' && b.count) prep.chips?.push(m.referenceDocumentsChip(b.count))
        break
      }

      case 'think':
        if (b.lane === MAIN_LANE && !b.live) openWork(b).thoughts += 1
        break
      case 'write':
        if (b.lane === MAIN_LANE && !b.live) openWork(b).drafts += 1
        break
      case 'tinker':
        if (b.lane === MAIN_LANE && !b.live) openWork(b).bench += 1
        break
      case 'call':
        if (b.lane === MAIN_LANE && !b.live) openWork(b).lookups += 1
        break
      case 'file':
        if (b.lane === MAIN_LANE) openWork(b).memory += 1
        break

      case 'brief_specialist':
      case 'specialist_start':
      case 'specialist_done': {
        flushWork()
        if (specialists && steps[steps.length - 1]?.id !== specialists.step.id && allLanesSettled(specialists)) {
          // A new burst after the previous team fully finished → a fresh step.
          specialists = null
        }
        if (!specialists) {
          const step: JourneyStep = {
            id: `team:${b.seq}`,
            icon: 'subagents',
            title: m.specialistTeamAtWork,
            atMs: b.atMs,
            status: 'active'
          }
          specialists = { step, lanes: new Map(), briefs: 0 }
          steps.push(step)
        }
        if (b.kind === 'brief_specialist') specialists.briefs += 1
        if (b.kind === 'specialist_start') specialists.lanes.set(b.lane, 'running')
        if (b.kind === 'specialist_done') specialists.lanes.set(b.lane, b.error ? 'failure' : 'success')
        refreshSpecialists(specialists, m)
        break
      }

      case 'wait_user': {
        flushWork()
        const last = steps[steps.length - 1]
        if (last && last.status === 'waiting') {
          // tool_use(request_human_input) followed by the awaiting envelope —
          // the same question twice; keep the richer of the two.
          if (!last.detail && promptFor(prompts, b.seq)) last.detail = promptFor(prompts, b.seq)
          break
        }
        questions += 1
        steps.push({
          id: `question:${b.seq}`,
          icon: 'prompt',
          title: questions > 1 ? m.askedYouAQuestionRound(questions) : m.askedYouAQuestion,
          ...(promptFor(prompts, b.seq) ? { detail: promptFor(prompts, b.seq) } : {}),
          atMs: b.atMs,
          status: 'waiting'
        })
        break
      }

      case 'user_note': {
        flushWork()
        // Close the open question, then record how it was answered.
        for (let i = steps.length - 1; i >= 0; i--) {
          const s = steps[i]
          if (s && s.status === 'waiting') {
            s.status = 'done'
            break
          }
        }
        const meta = replies.get(b.seq)
        const trigger = meta?.trigger ?? 'reply'
        steps.push(
          trigger === 'timeout'
            ? {
                id: `reply:${b.seq}`,
                icon: 'history',
                title: m.noReplyInTime,
                atMs: b.atMs,
                status: 'done'
              }
            : trigger === 'cancel'
              ? { id: `reply:${b.seq}`, icon: 'cancel', title: m.cancelledByYou, atMs: b.atMs, status: 'done' }
              : {
                  id: `reply:${b.seq}`,
                  icon: 'edit',
                  title: m.youReplied,
                  ...(meta?.replyPreview ? { detail: oneLine(meta.replyPreview, 140) } : {}),
                  atMs: b.atMs,
                  status: 'done'
                }
        )
        break
      }

      case 'stumble': {
        const last = steps[steps.length - 1]
        if (last && retryCounts.has(last.id)) {
          const n = (retryCounts.get(last.id) ?? 1) + 1
          retryCounts.set(last.id, n)
          last.atMs = b.atMs
          last.title = m.hitASnagTimes(n)
          break
        }
        flushWork()
        const step: JourneyStep = {
          id: `retry:${b.seq}`,
          icon: 'refresh',
          title: m.hitASnag,
          atMs: b.atMs,
          status: 'done'
        }
        retryCounts.set(step.id, 1)
        steps.push(step)
        break
      }

      case 'pack':
        flushWork()
        steps.push({
          id: `pack:${b.seq}`,
          icon: 'file',
          title: m.packagedDeliverable,
          ...(b.count ? { chips: [m.filesChip(b.count)] } : {}),
          atMs: b.atMs,
          status: 'done'
        })
        break

      case 'deliver':
        flushWork()
        if (specialists) refreshSpecialists(specialists, m, true)
        steps.push({
          id: `deliver:${b.seq}:${b.atMs}`,
          icon: 'check',
          title: b.partial ? m.deliveredWithNotes : m.delivered,
          atMs: b.atMs,
          status: 'success'
        })
        break

      case 'collapse':
        flushWork()
        if (specialists) refreshSpecialists(specialists, m, true)
        steps.push({
          id: `failed:${b.seq}:${b.atMs}`,
          icon: 'x',
          title: m.runFailedTitle,
          detail: m.runFailedDetail,
          ...(b.chip ? { chips: [b.chip] } : {}),
          atMs: b.atMs,
          status: 'error'
        })
        break

      // Ambient beats that don't merit a history entry of their own.
      case 'sit_desk':
      case 'tool_done':
      case 'sparkle':
      case 'stretch':
        break
      default:
        break
    }
  }

  flushWork()

  // Live: the most recent step is the one happening now (a waiting question
  // stays "waiting" — that's already the stronger signal).
  if (opts.live) {
    const last = steps[steps.length - 1]
    if (last && last.status === 'done') last.status = 'active'
  } else {
    // Finished stream with a dangling active team (e.g. record truncation).
    if (specialists) refreshSpecialists(specialists, m, true)
    for (const s of steps) {
      if (s.status === 'active') s.status = 'done'
      if (s.status === 'waiting') s.status = 'done'
    }
  }

  return steps
}

/** One HITL question the agent asked, in conversation order. */
export interface AskedQuestion {
  /** 1-based round — pairs with `RunInputReplyView.index + 1`. */
  round: number
  prompt: string
  rationale?: string
  atMs: number
}

/**
 * The questions the agent asked (request_human_input tool_use events), in
 * order — lets the request card show each reply UNDER the question that
 * prompted it instead of as a context-free answer.
 */
export function questionsAsked(events: TraceEvent[]): AskedQuestion[] {
  const out: AskedQuestion[] = []
  for (const e of events) {
    if (e.kind !== 'tool_use' || e.meta.name !== 'request_human_input') continue
    const input = (e.meta.input ?? {}) as { prompt?: unknown; rationale?: unknown }
    out.push({
      round: out.length + 1,
      prompt: typeof input.prompt === 'string' ? input.prompt : '',
      ...(typeof input.rationale === 'string' ? { rationale: input.rationale } : {}),
      atMs: e.atMs
    })
  }
  return out
}

/** The latest stage direction — a business-readable "now" line for status bars. */
export function currentActivity(events: TraceEvent[], strings?: BeatStrings): string | null {
  const beats = eventsToBeats(events, strings)
  for (let i = beats.length - 1; i >= 0; i--) {
    const caption = beats[i]?.caption
    if (caption) return caption
  }
  return null
}

/** Aggregate counts for the summary strip on the result card. */
export function runStats(events: TraceEvent[]): RunStats {
  const beats = eventsToBeats(events)
  let sources = 0
  let attachmentsIn = 0
  let workItems = 0
  let retries = 0
  const questionKeys = new Set<string>()
  let first: number | null = null
  let last: number | null = null
  let prevWasQuestion = false
  for (const b of beats) {
    if (first === null || b.atMs < first) first = b.atMs
    if (last === null || b.atMs > last) last = b.atMs
    switch (b.kind) {
      case 'fetch_books':
        sources += b.count ?? 0
        break
      case 'collect_mail':
        attachmentsIn += b.count ?? 0
        break
      case 'tinker':
      case 'call':
        if (!b.live) workItems += 1
        break
      case 'stumble':
        retries += 1
        break
      case 'wait_user':
        // Same consecutive-dedupe rule as the journey (tool_use + envelope).
        if (!prevWasQuestion) questionKeys.add(b.key ?? `q:${b.seq}`)
        break
      default:
        break
    }
    prevWasQuestion = b.kind === 'wait_user'
  }
  return {
    durationMs: first !== null && last !== null && last > first ? last - first : null,
    questions: questionKeys.size,
    specialists: agentsInOrder(events).length,
    sources,
    attachmentsIn,
    workItems,
    retries
  }
}

// ── internals ────────────────────────────────────────────────────────────────

function workStep(w: WorkAcc, m: JourneyStrings): JourneyStep | null {
  const parts: string[] = []
  if (w.thoughts > 0) parts.push(m.workAnalysis)
  if (w.drafts > 0) parts.push(m.workDrafting)
  if (w.bench > 0) parts.push(m.workHandsOnTasks(w.bench))
  if (w.lookups > 0) parts.push(m.workOutsideLookups(w.lookups))
  if (w.memory > 0) parts.push(m.workNotesUpdated)
  if (parts.length === 0) return null
  return {
    id: `work:${w.seq}`,
    icon: 'bolt',
    title: m.working,
    detail: parts.join(' · '),
    atMs: w.atMs,
    status: 'done'
  }
}

function refreshSpecialists(acc: SpecialistsAcc, m: JourneyStrings, forceClose = false): void {
  const total = Math.max(acc.lanes.size, acc.briefs)
  const done = [...acc.lanes.values()].filter((v) => v !== 'running').length
  const failed = [...acc.lanes.values()].filter((v) => v === 'failure').length
  const settled = forceClose || (total > 0 && acc.lanes.size >= total && done === acc.lanes.size)
  acc.step.title = settled ? m.specialistTeamDone(total) : m.specialistTeamWorking(total)
  acc.step.detail = total > 0 ? m.specialistProgress(done, total, failed) : undefined
  acc.step.chips = [...acc.lanes.keys()].slice(0, 4).map(shortAgent)
  if (acc.lanes.size > 4) acc.step.chips.push(m.moreChip(acc.lanes.size - 4))
  acc.step.status = settled ? 'done' : 'active'
}

function allLanesSettled(acc: SpecialistsAcc): boolean {
  if (acc.lanes.size === 0) return false
  for (const v of acc.lanes.values()) if (v === 'running') return false
  return true
}

/** seq → the question text of request_human_input tool_use events. */
function questionPromptsBySeq(events: TraceEvent[]): Map<number, string> {
  const out = new Map<number, string>()
  for (const e of events) {
    if (e.kind !== 'tool_use' || e.meta.name !== 'request_human_input') continue
    const input = e.meta.input
    if (input && typeof input === 'object' && typeof (input as { prompt?: unknown }).prompt === 'string') {
      out.set(e.seq, (input as { prompt: string }).prompt)
    }
  }
  return out
}

function promptFor(prompts: Map<number, string>, seq: number): string | undefined {
  const p = prompts.get(seq)
  return p ? oneLine(p, 160) : undefined
}

/** seq → user_reply meta (trigger + preview). */
function replyMetaBySeq(events: TraceEvent[]): Map<number, { trigger: string; replyPreview?: string }> {
  const out = new Map<number, { trigger: string; replyPreview?: string }>()
  for (const e of events) {
    if (e.kind !== 'user_reply') continue
    out.set(e.seq, {
      trigger: e.meta.trigger,
      ...(e.meta.replyPreview ? { replyPreview: e.meta.replyPreview } : {})
    })
  }
  return out
}

function oneLine(s: string, max: number): string {
  const flat = s.replace(/\s+/g, ' ').trim()
  return flat.length <= max ? flat : `${flat.slice(0, max - 1).trimEnd()}…`
}
